import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TbArrowRight, TbCreditCard, TbArrowsExchange } from 'react-icons/tb';
import LineChart from './LineChart';
import Button from '../buttons/Button';

interface BankAccountsChartProps {
  bankAccounts: any[];
  onCreateAccount: () => void;
}

function BankAccountsChart({ bankAccounts, onCreateAccount }: BankAccountsChartProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.overview.bankAccountsChart' });
  const navigate = useNavigate();

  // Mock data generation for each bank account - always 365 days
  const mockData = useMemo(() => {
    const now = new Date();
    const days = 365; // Always 1 year with daily granularity

    // Generate separate data for each bank account
    return bankAccounts.map((account, accountIndex) => {
      const data = [];
      const baseBalance = account.balance;

      for (let i = days; i >= 0; i -= 1) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);

        // Simulate balance fluctuations for each account
        const variation = Math.sin(i * 0.1 + accountIndex) * 2000 + Math.random() * 1000 - 500;
        const balance = Math.max(0, baseBalance + variation);

        data.push({
          date: date.toISOString(),
          balance: Math.round(balance),
          accountName: account.accountName,
        });
      }

      return {
        accountName: account.accountName,
        data,
        color: accountIndex === 0 ? '#2563eb' : '#059669', // Blue for first, green for second
      };
    });
  }, [bankAccounts]);

  const handleSeeMore = () => {
    navigate('/financial/administration', { state: { activeTab: 'bankAccounts' } });
  };

  // Check if any accounts are synced
  const hasSyncedAccounts = bankAccounts.some(
    (account) => account.requisitionId && account.transferCompleted,
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('title')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={handleSeeMore}
          className="flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary/5">
          <span>{t('seeAllAccounts')}</span>
          <TbArrowRight size={16} />
        </button>
      </div>

      {/* Body */}
      {bankAccounts.length === 0 ? (
        <div className="py-12 text-center">
          <TbCreditCard className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">{t('empty.title')}</h3>
          <p className="mb-6 text-sm text-gray-500">{t('empty.subtitle')}</p>
          <Button variant="primary-dark" onClick={onCreateAccount}>
            {t('empty.cta')}
          </Button>
        </div>
      ) : !hasSyncedAccounts ? (
        <div className="py-12 text-center">
          <TbArrowsExchange className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">{t('empty.syncTitle')}</h3>
          <p className="mb-6 text-sm text-gray-500">{t('empty.syncSubtitle')}</p>
          <Button
            variant="primary-dark"
            onClick={() => {
              if (bankAccounts.length > 0) {
                navigate(`/financial/mutations/${bankAccounts[0].accountId}`);
              } else {
                navigate('/financial/bank-accounts');
              }
            }}>
            {t('empty.ctaSync')}
          </Button>
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="mb-6 flex items-center justify-end">
            <div className="flex items-center space-x-6">
              {bankAccounts.map((account, index) => (
                <div key={account.accountId} className="flex items-center space-x-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: index === 0 ? '#2563eb' : '#059669' }}
                  />
                  <span className="text-sm text-gray-600">{account.accountName}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div className="w-full">
            <LineChart data={[]} series={mockData} height={300} />
          </div>
        </>
      )}
    </div>
  );
}

export default BankAccountsChart;

